import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useMemo } from 'react';

import type { TimePeriod } from 'app/components/pages/Extranet/ExtranetInventory/Legacy/type';
import { useTabInventoryState } from 'app/components/pages/Extranet/ExtranetInventory/Modern/hooks';
import { computePacks } from 'app/components/pages/Extranet/ExtranetInventory/Modern/utils/packs';
import type { InventoryExperiencesForm } from 'app/typings/core/extranet/inventory/form/inventoryForm';
import type { InventoryResponse } from 'app/typings/core/extranet/inventory/inventory';
import { formatDateWithoutHour } from 'app/utils/dates';
import { apiMutation } from 'app/utils/request/api';

import { CORE_QUERY_KEYS, ENDPOINTS } from '..';

export function useSaveExperiences(hotelId?: number, timePeriod?: TimePeriod) {
  const queryClient = useQueryClient();
  const { setInventory, dependencies } = useTabInventoryState();

  const formattedQueryParams = useMemo(() => {
    return {
      startDate: formatDateWithoutHour(timePeriod?.startDate ?? new Date()),
      endDate: formatDateWithoutHour(timePeriod?.endDate ?? new Date()),
    };
  }, [timePeriod?.startDate, timePeriod?.endDate]);

  return useMutation({
    mutationFn: async (payload: InventoryExperiencesForm) =>
      hotelId && timePeriod
        ? (apiMutation(
            'POST',
            {
              path: ENDPOINTS.saveExperiences,
              params: { hotelId: hotelId.toString() },
              queryParams: formattedQueryParams,
            },
            payload
          ) as Promise<InventoryResponse>)
        : Promise.reject(null),
    onSuccess: (response) => {
      message.success('Experiences successfully saved');

      queryClient.setQueryData(
        [CORE_QUERY_KEYS.inventory, { hotelId, ...formattedQueryParams }],
        (oldData: InventoryResponse | undefined) => {
          if (!oldData) {
            return response;
          }

          const newData = Object.entries(oldData).reduce(
            (acc, [date, dateData]) => {
              acc[date] = {
                ...dateData,
                experiences:
                  response[date]?.experiences ?? dateData.experiences,
              };

              return acc;
            },
            {} as InventoryResponse
          );

          if (dependencies) {
            const newInventory = computePacks(
              formatDateWithoutHour(dependencies.saleEndDate),
              formattedQueryParams.startDate,
              formattedQueryParams.endDate,
              dependencies.packData,
              dependencies.filteredExperiences,
              [],
              newData,
              dependencies.hasChannelManager,
              dependencies.hasAutoInventory
            );

            setInventory(newInventory);
          }

          return newData;
        }
      );

      queryClient.invalidateQueries({
        queryKey: [CORE_QUERY_KEYS.packScore, { hotelId }],
      });
    },
    onError: () => {
      message.error('Failed to save experiences.');
    },
  });
}
